import { FC } from 'react';

import { Link } from '@inertiajs/react';
import { Package } from 'lucide-react';

import { OrderConfirmationHeaderCard } from './OrderConfirmationHeaderCard';
import { OrderPlacedDetailsCard } from './OrderPlacedDetailsCard';
import { Button } from '../../components/ui/Button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '../../components/ui/Card';
import { Routes } from '../../models/routes';
import { Order } from '../../types';

interface Props {
    orders: Order[];
}

function getOrderNumber(order: Order) {
    return order.number || order.id;
}

function getFinalTotal(order: Order) {
    return order.total_price || order.final_total || order.total || 0;
}

const getItemsCount = (order: Order) => {
    if (!order.order_items || !order.order_items.length) return 0;
    return order.order_items.reduce(
        (sum: number, item) => sum + (item.amount || 0),
        0,
    );
};

const Index: FC<Props> = ({ orders }) => {
    const totalAmount = orders.reduce(
        (sum, ord) => sum + getFinalTotal(ord),
        0,
    );
    const totalItemsCount = orders.reduce(
        (sum, ord) => sum + getItemsCount(ord),
        0,
    );
    const isMultipleOrders = orders.length > 1;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-muted/50">
            <div className="container mx-auto px-4 py-8">
                <div className="mx-auto max-w-2xl">
                    <OrderConfirmationHeaderCard
                        orders={orders}
                        isMultipleOrders={isMultipleOrders}
                        getOrderNumber={getOrderNumber}
                    />

                    <div className="mb-8 space-y-6">
                        {isMultipleOrders && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Package className="h-5 w-5" />
                                        Загальна сума всіх замовлень
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-600">
                                                Кількість товарів:
                                            </span>
                                            <span className="font-medium">
                                                {totalItemsCount} шт.
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-600">
                                                Загальна сума:
                                            </span>
                                            <span className="text-lg font-bold">
                                                ₴{totalAmount.toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {orders.map((order, index) => (
                            <OrderPlacedDetailsCard
                                key={order.id}
                                order={order}
                                index={index}
                                isMultipleOrders={isMultipleOrders}
                                getOrderNumber={getOrderNumber}
                                getItemsCount={getItemsCount}
                                getFinalTotal={getFinalTotal}
                            />
                        ))}
                    </div>

                    <Card className="mb-8">
                        <CardHeader>
                            <CardTitle>Що далі?</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-3">
                                    <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                                        <span className="text-xs font-bold text-blue-600">
                                            1
                                        </span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-muted-foreground">
                                            Підтвердження замовлення
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-muted-foreground/60">
                                            {isMultipleOrders
                                                ? "Продавці зв'яжуться з вами для підтвердження деталей замовлень"
                                                : "Продавець зв'яжеться з вами для підтвердження деталей замовлення"}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                                        <span className="text-xs font-bold text-blue-600">
                                            2
                                        </span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-muted-foreground">
                                            Підготовка до відправки
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-muted-foreground/60">
                                            {isMultipleOrders
                                                ? 'Ваші замовлення будуть зібрані та підготовлені продавцями до відправки'
                                                : 'Ваше замовлення буде зібрано та підготовлено продавцем до відправки'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                                        <span className="text-xs font-bold text-blue-600">
                                            3
                                        </span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-muted-foreground">
                                            Доставка
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-muted-foreground/60">
                                            Ви отримаєте SMS з трек-номером для
                                            відстеження посилки
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-8">
                        <CardHeader>
                            <CardTitle>Потрібна допомога?</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div className="flex items-center space-x-3">
                                    <span className="h-5 w-5 text-gray-400">
                                        📧
                                    </span>
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-muted-foreground">
                                            Email підтримка
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-muted-foreground/60">
                                            carton.market.ua@gmail.com
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <p className="mt-4 text-sm text-gray-500 dark:text-muted-foreground/50">
                                Час роботи: Пн-Пт 9:00-18:00, Сб 10:00-16:00
                            </p>
                        </CardContent>
                    </Card>

                    <div className="flex flex-col justify-center gap-4 sm:flex-row">
                        <Button
                            asChild
                            size="lg"
                            className="flex items-center space-x-2"
                        >
                            <Link
                                href={Routes.Home}
                                className="flex items-center space-x-2"
                            >
                                <Package className="h-4 w-4" />
                                <span>Повернутися на головну</span>
                            </Link>
                        </Button>
                        <Button
                            variant="outline"
                            size="lg"
                            asChild
                            className="bg-transparent"
                        >
                            <Link
                                href={Routes.Purchases}
                                className="flex items-center space-x-2"
                            >
                                <span>Мої покупки</span>
                            </Link>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Index;
